import { useToast } from '@ui/toasts/useToastManager'
import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import Button from '@ui/buttons/Button'
import PayNowButton from 'features/payment/PayNow'
import {
  CofidisApplicationFormPayload,
  CofidisApplicationFormPayloadSchema,
  DeviceProviderSchema,
  getPayRange,
  NumberUtils,
  PaymentUtils,
} from '@ca/common'
import Page from '@shell/Page'
import Slider from '@ui/components/Slider'
import DetailRow from '@ui/elements/DetailRow'
import Header from '@ui/elements/Header'
import IconPercentage from '@ui/elements/Icon/components/IconPercentage'
import PriceComponent from '@/price/Price'
import { FormCheckbox } from '@ui/forms/Checkbox'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import { usePaymentMethodFee } from '@order/hooks/useInvoiceFee'
import useOrderConfigure from '@order/hooks/useOrderConfigure'
import {
  getDefaultProvider,
  resolveProvider,
  usePaymentMethodSettings,
} from '@order/hooks/usePaymentMethods'
import usePayments, { PaymentDetails } from '@order/hooks/usePayments'
import useOrderSubmit from '@order/hooks/useOrderSubmit'
import usePaymentStatusAmounts from '@order/hooks/usePaymentStatusAmounts'
import ReaderSelect from '@screens/payment/settings/ReaderSelect'
import SignatureModal from '@screens/payment/settings/SignatureModal'
import usePaymentAmountSlider from '@screens/payment/settings/usePaymentAmountSlider'
import usePaymentForm, {
  PaymentFormValues,
} from '@screens/payment/settings/usePaymentForm'
import InstallmentDetails from '@screens/payment/status/installment/InstallmentDetails'
import Typography from '@ui/typography'
import { trpc } from 'api'
import { router } from 'expo-router'
import Analytics from 'hooks/analytics/Analytics'
import { useCurrencyDecimals, usePrice } from 'hooks/useCurrency'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { INPUT_MASK } from '@ui/utils/masks'
import { usePaymentDetails } from './usePaymentDetails'
import InputsSection, { BorderSection } from '@ui/forms/InputsSection'
import usePaymentTranslations from '@order/hooks/usePaymentTranslations'
import HelpText from '@ui/elements/HelpText'
import PaymentHelpButton from '@screens/payment/PaymentHelpButton'
import PaymentActionsModal from 'features/payment/modals/PaymentActions/PaymentActionsModal'
import CheckRefundBalanceModal from 'features/payment/modals/CheckRefundBalance/CheckRefundBalanceModal'
import type { i18n } from 'i18next'
import CofidisMonthlyApplicationForm, {
  CofidisMonthlyApplicationFormProps,
} from 'features/payment/cofidisMonthlyApplication/CofidisMonthlyApplicationForm'
import { SectionModalLayout } from 'features/payment/cofidisMonthlyApplication'
import { CofidisApplicationForm } from 'features/payment/cofidisMonthlyApplication/useCofidisApplicationForm'
import useCofidisApplicationFormDefaults from 'features/payment/cofidisMonthlyApplication/useCofidisApplicationFormDefaults'
import { StatusProps } from '@ui/elements/Status/types'
import GlobeMedAddModal from 'features/payment/modals/GlobeMed/GlobeMedAddModal'
import useGlobemedPayment from 'features/payment/globemed/useGlobemedPayment'
import GlobeMedRemoveModal from 'features/payment/modals/GlobeMed/GlobeMedRemoveModal'

const normalize = (
  value: number,
  {
    min,
    max,
  }: {
    min: number
    max: number
  }
) => {
  return Math.max(min, Math.min(value, max))
}

const CofidisApplicationFormComponent = (
  props: Omit<
    CofidisMonthlyApplicationFormProps,
    'defaultValues' | 'LayoutComponent'
  >
) => {
  const defaultValues = useCofidisApplicationFormDefaults()
  return (
    <CofidisMonthlyApplicationForm
      defaultValues={defaultValues}
      LayoutComponent={SectionModalLayout}
      {...props}
    />
  )
}

const consents = {
  terms: 'terms',
  privacy: 'privacy',
  sepa: 'sepa',
} as const

const parse = NumberUtils.parse.toFloat

type PrepaymentVariant = 'required' | 'fullPayment' | 'completed' | 'available'

type PrepaymentVariantConfig = {
  badgeKey: string
  descKey: string
  statusVariant: StatusProps['variant']
  showSlider: boolean
  matches: (params: { min: number; max: number }) => boolean
}

const prepaymentVariantConfig: Record<
  PrepaymentVariant,
  PrepaymentVariantConfig
> = {
  required: {
    badgeKey: 'payment:settings.amount.variant.required.badge',
    descKey: 'payment:settings.amount.variant.required.desc',
    statusVariant: 'attention',
    showSlider: true,
    matches: () => true,
  },
  fullPayment: {
    badgeKey: 'payment:settings.amount.variant.fullPayment.badge',
    descKey: 'payment:settings.amount.variant.fullPayment.desc',
    statusVariant: 'danger',
    showSlider: false,
    matches: ({ min, max }) => min === max,
  },
  completed: {
    badgeKey: 'payment:settings.amount.variant.completed.badge',
    descKey: 'payment:settings.amount.variant.completed.desc',
    statusVariant: 'success',
    showSlider: false,
    matches: ({ min, max }) => min === 0 && max === 0,
  },
  available: {
    badgeKey: 'payment:settings.amount.variant.available.badge',
    descKey: 'payment:settings.amount.variant.available.desc',
    statusVariant: 'active',
    showSlider: true,
    matches: ({ min, max }) => min === 0 && max > 0,
  },
}

const prepaymentVariantPriority: PrepaymentVariant[] = [
  'completed',
  'available',
  'fullPayment',
  'required',
]

const resolvePrepaymentVariant = ({
  min,
  max,
}: {
  min: number
  max: number
}): PrepaymentVariant => {
  return (
    prepaymentVariantPriority.find((variant) =>
      prepaymentVariantConfig[variant].matches({ min, max })
    ) ?? 'required'
  )
}

const getPaymentErrorMessage = (
  i18n: i18n,
  errorCode: string | undefined,
  fallbackMessage: string | undefined
): string => {
  if (errorCode) {
    const translationKey = `toasts:error.payment.${errorCode}`
    if (i18n.exists(translationKey)) {
      return i18n.t(translationKey)
    }
  }
  return fallbackMessage || i18n.t('toasts:error.default.desc')
}

export default function PaymentSettings({ item }: { item: PaymentDetails }) {
  const { t, i18n } = useTranslation([
    'modals',
    'ui',
    'types',
    'payment',
    'toasts',
  ])
  const text = 'payment:settings'
  const rows = 'payment:status.details'

  const [signatureModalOpen, setSignatureModalOpen] = useState(false)
  const [actionsModalOpen, setActionsModalOpen] = useState(false)
  const [checkRefundBalanceOpen, setCheckRefundBalanceOpen] = useState(false)
  const [globemedOpen, setGlobemedOpen] = useState(false)
  const [globemedRemoveOpen, setGlobemedRemoveOpen] = useState(false)
  const metadata = usePayments(
    (s) => s.payments[item.localId]?.metadata ?? null
  )

  const isAmountFieldFocused = useRef(false)
  const isAmountTouched = useRef(false)
  const amountOnFocus = useRef<string | null>(null)
  const cofidisApplicationFormRef = useRef<CofidisApplicationForm>(null)

  const {
    defaultMethod,
    payment,
    methods,
    orderIds,
    getValues,
    items,
    orders,
    discounts,
    merged,
    scenarios,
  } = usePaymentDetails(item)
  const terminalProvider = DeviceProviderSchema.parse(
    resolveProvider('terminal')
  )
  const devices = trpc.order.payments.devices.useQuery({
    provider: terminalProvider,
  })

  const globemed = useGlobemedPayment({
    orderIds,
    localId: item.localId,
    metadata,
  })
  const globemedAmount = globemed.amount

  const form = usePaymentForm({
    defaultMethod,
    getPaymentValues: getValues,
    defaultReaderId: devices.data?.[0]?.reader_id ?? null,
    metadataPayRange: metadata?.pay_range ?? null,
    fetchedPayRange: globemed.freshMetadata?.pay_range ?? null,
    provider: payment.provider,
    scenarios,
  })

  const method = useWatch({ name: 'method', control: form.control })

  const values = useMemo(() => getValues(method), [getValues, method])
  const { toPay } = values
  const updatePaymentMethod = usePayments((s) => s.updatePaymentMethod)
  const handlePaymentResponse = usePayments((s) => s.handlePaymentResponse)
  const handlePaymentSuccess = usePayments((s) => s.handlePaymentSuccess)

  const amount = useWatch({ name: 'amount', control: form.control })
  const { fee } = usePaymentMethodFee(method)

  const configure = useOrderConfigure(false)
  const toast = useToast()

  const settings = usePaymentMethodSettings(method || defaultMethod, {
    provider: payment.provider,
    scenarios,
  })

  const currentProvider = useMemo(() => {
    return getDefaultProvider(method || payment.method)
  }, [method, payment.method])

  const currencyDecimals = useCurrencyDecimals()
  const isUnzer = currentProvider === 'unzer'
  const unzerPlan = trpc.order.payments.unzerPlan.useQuery(
    { orderIds },
    { enabled: isUnzer && method === 'monthly' && orderIds.length > 0 }
  )

  const { min, max } = PaymentUtils.getMinMax({
    toPay,
    fee,
    custom: settings?.custom,
    decimals: currencyDecimals,
  })

  // Stored metadata applies to every method; fetched metadata only where staff
  // picks the amount, since fixed amounts may carry client-side fees
  const metadataRange = getPayRange(
    metadata?.pay_range ??
      (settings?.custom === 'enabled'
        ? globemed.freshMetadata?.pay_range
        : undefined)
  )
  const minResolved = metadataRange?.[0] ?? min
  const maxResolved = metadataRange?.[1] ?? max

  // Metadata balance includes GlobeMed, so split it out of the refund row
  const refundBalance =
    metadata !== null
      ? Math.max(
          0,
          NumberUtils.round.decimal(
            max - metadata.rest_amount - (globemedAmount ?? 0),
            currencyDecimals
          )
        )
      : 0
  // GlobeMed may cover up to the current range maximum, without client-side fees
  const globemedMax =
    getPayRange(
      metadata?.pay_range ?? globemed.freshMetadata?.pay_range
    )?.[1] ?? toPay

  const showRefundBalance =
    metadata !== null &&
    !globemed.hasUnresolvedChange &&
    (globemedAmount === null || refundBalance > 0)

  const optionsMethods = useMemo(() => {
    return methods.map((method) => ({
      value: method,
      label: t(`types:payment.method.${method}`),
    }))
  }, [methods, t])

  const prepayment = useMemo(
    () =>
      normalize(parse(amount || ''), { min: minResolved, max: maxResolved }),
    [amount, minResolved, maxResolved]
  )
  const onCollectValue = useMemo(
    () => maxResolved - prepayment,
    [maxResolved, prepayment]
  )

  const orderSubmit = useOrderSubmit({
    onSuccess: () => {
      handlePaymentSuccess(item.localId)
      router.navigate(`/payment/status/${item.localId}`)
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('toasts:error.payment.title'),
        text: getPaymentErrorMessage(i18n, err.data?.cause?.code, err.message),
      })
    },
  })

  const paymentMutation = trpc.order.payments.start.useMutation({
    onSuccess: (res, req) => {
      Analytics.logEvent('payment_start', {
        customAmount: `${req.customAmount}`,
        orders: req.orderIds.join(','),
      })
      setSignatureModalOpen(false)
      handlePaymentResponse(item.localId, res)
      router.navigate(`/payment/status/${item.localId}`)
    },
    onError: (err, req) => {
      Analytics.logEvent('payment_error', {
        message: err.message,
        customAmount: `${req.customAmount}`,
        orders: req.orderIds.join(','),
      })
      toast({
        variant: 'error',
        title: t('toasts:error.payment.title'),
        text: getPaymentErrorMessage(i18n, err.data?.cause?.code, err.message),
      })
    },
  })

  const { key } = usePaymentTranslations({
    method,
    provider: resolveProvider(method),
  })

  const tOption = `${text}.method.options.${key}`
  const methodHelpKey =
    isUnzer && method === 'monthly'
      ? `${text}.method.options.installment.unzer.help`
      : `${tOption}.help`

  const handleSignatureSave = (sign: string) => {
    const values = form.getValues()
    handlePayment({ ...values, sign })
  }

  const handlePayment = (
    values: PaymentFormValues & {
      sign?: string
      cofidisApplication?: CofidisApplicationFormPayload
    }
  ) => {
    const provider = getDefaultProvider(values.method)
    if (provider === 'unzer' && !unzerPlan.data) {
      void unzerPlan.refetch()
      toast({
        variant: 'error',
        title: t('toasts:error.payment.title'),
        text: getPaymentErrorMessage(i18n, undefined, unzerPlan.error?.message),
      })
      return
    }

    if (settings?.signature && !values.sign) {
      setSignatureModalOpen(true)
      return
    }

    const frameIds = items
      .map((item) => item.frame?.id)
      .filter((id) => !!id) as number[]

    const amount = parse(values.amount)

    const data = {
      ...values,
      amount: normalize(amount, { min: minResolved, max: maxResolved }),
      // Currently we don't support multiple providers for the same method
      // We could support it by adding a new field to the payment form or having provider along with method
      provider: resolveProvider(values.method),
      reader_id:
        values.method === 'terminal'
          ? values.reader_id ?? devices.data?.[0]?.reader_id ?? undefined
          : undefined,
    }
    updatePaymentMethod(item.localId, data)

    if (data.amount === 0) {
      orderSubmit.mutate(orderIds)
      return
    }
    if (!provider) {
      toast({
        variant: 'error',
        title: t('toasts:error.payment.title'),
        // TODO: Translate
        text: 'No provider for this method is available',
      })
      return
    }

    const payload = {
      orderIds,
      extra: {
        orders: orders.filter((order) => !!order.data),
        discounts,
        frameIds,
      },
      merged: merged && data.method === 'terminal' ? true : false,
      paymentMethod: data.method,
      // Currently we don't support multiple providers for the same method
      // If we need to support it, we need to add a new field to the payment form
      // And change how this resolved
      provider,
      customAmount: data.amount,
      reader_id: data.reader_id,
      sign: data.sign,
      // Remove spaces from IBAN
      iban: data.iban?.replaceAll(' ', ''),
      holder: data.holder,
      inquiryId: provider === 'unzer' ? unzerPlan.data?.inquiry_id : undefined,
      cofidisApplication: data.cofidisApplication,
    }

    paymentMutation.mutate(payload)
  }

  const isCustomAmount = settings?.custom === 'enabled'
  const isCustomAmountDisabled = settings?.custom === 'disabled'

  const minPrice = usePrice(minResolved)
  const maxPrice = usePrice(maxResolved)
  const amountSlider = usePaymentAmountSlider(maxResolved)
  const isAmountValid = !form.formState.errors.amount

  const isIbanEnabled = settings?.iban
  const isPayment = method !== 'monthly'
  const isInstallment = method === 'monthly'

  const nowPercent = NumberUtils.percentage.getSafe(prepayment, maxResolved)
  const laterPercent = NumberUtils.percentage.getSafe(
    onCollectValue,
    maxResolved
  )

  const prepaymentVariant = useMemo(
    () => resolvePrepaymentVariant({ min: minResolved, max: maxResolved }),
    [minResolved, maxResolved]
  )

  const currentVariantConfig = prepaymentVariantConfig[prepaymentVariant]

  const amountPercentText =
    prepaymentVariant === 'fullPayment'
      ? `${Math.round(nowPercent)}% ${t('payment:settings.amount.now')}`
      : `${Math.round(nowPercent)}% ${t(
          'payment:settings.amount.now'
        )}, ${Math.round(laterPercent)}% ${t('payment:settings.amount.later')}`

  const prepaymentStatusProps = useMemo<StatusProps>(() => {
    return {
      text: t(currentVariantConfig.badgeKey),
      variant: currentVariantConfig.statusVariant,
      className: 'self-start',
    }
  }, [currentVariantConfig.badgeKey, currentVariantConfig.statusVariant, t])

  const amountDescription = t(currentVariantConfig.descKey)

  const handleCofidisApplicationSubmit = () => {
    cofidisApplicationFormRef.current?.handleSubmit((cofidisValues) => {
      const payload =
        CofidisApplicationFormPayloadSchema.safeParse(cofidisValues)
      if (!payload.success) {
        toast({
          variant: 'error',
          title: t('toasts:error.payment.title'),
          // TODO: Add more specific error message
        })
        return
      }
      form.handleSubmit(
        (v) =>
          handlePayment({
            ...v,
            cofidisApplication: payload.data,
          }),
        console.warn
      )()
    }, console.warn)()
  }

  const onPress = () => {
    if (currentProvider === 'cofidisApplication') {
      // Resolve cofidis application form values to payload
      handleCofidisApplicationSubmit()
    } else {
      form.handleSubmit(handlePayment, console.warn)()
    }
  }

  const enabledConsents = useMemo(() => {
    return Object.values(consents).filter((consent) => {
      return settings[consent]
    })
  }, [settings])

  useEffect(() => {
    if (!isCustomAmount) {
      form.setValue('amount', `${maxResolved}`)
      form.clearErrors('amount')
      return
    }
    // Only correct the value when the field is not focused to allow free typing
    if (isCustomAmount && !isAmountFieldFocused.current) {
      const parsed = parse(amount || '')
      if (parsed < minResolved) {
        form.setValue('amount', `${minResolved}`, { shouldValidate: false })
        form.clearErrors('amount')
      }
      if (parsed > maxResolved) {
        form.setValue('amount', `${maxResolved}`, { shouldValidate: false })
        form.clearErrors('amount')
      }
    }
  }, [isCustomAmount, minResolved, maxResolved, amount, form])

  // An untouched amount follows the minimum when the range changes (e.g. after GlobeMed)
  useEffect(() => {
    if (
      !isCustomAmount ||
      isAmountTouched.current ||
      isAmountFieldFocused.current
    ) {
      return
    }
    form.setValue('amount', `${minResolved}`, { shouldValidate: false })
    form.clearErrors('amount')
  }, [isCustomAmount, minResolved, form])

  const amounts = usePaymentStatusAmounts({
    payment,
    values,
  })

  const hasPaymentRows =
    settings?.rows?.prepayment !== false ||
    settings?.rows?.collect !== false ||
    !!fee

  const hasTerminal = methods.some((method) => method === 'terminal')

  const paymentDisplayIds = useMemo(() => {
    return orders.map((order) => order.name)
  }, [orders])

  const isPayNow = !['monthly', 'invoce'].includes(method)
  const isContinueLabel = metadata !== null && metadata.rest_amount === 0
  const ctaLoading =
    paymentMutation.isPending ||
    configure.isLoading ||
    orderSubmit.isPending ||
    globemed.metadataQuery.isFetching ||
    (isUnzer && unzerPlan.isFetching)

  return (
    <>
      <Page stage="payment">
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        <View className="flex flex-col gap-8 px-8">
          <InputsSection
            group
            title={t(`${text}.method.title`)}
            desc={t(
              `${text}.method.${
                optionsMethods.length > 1 ? 'desc' : 'descSingle'
              }`
            )}
            className="pb-8"
          >
            <View className="flex flex-col">
              <View className="flex flex-col gap-0 px-8">
                <FormSelect
                  name="method"
                  control={form.control}
                  options={optionsMethods}
                  label={t(`${text}.method.label`)}
                />
                <HelpText text={t(methodHelpKey)} />
              </View>
              {hasTerminal && (
                <ReaderSelect control={form.control} method={method} />
              )}
            </View>
          </InputsSection>

          {settings?.custom !== 'hidden' && (
            <InputsSection
              title={t(`${text}.amount.title`)}
              desc={amountDescription}
              statusProps={prepaymentStatusProps}
            >
              <FormInput
                name="amount"
                control={form.control}
                label={t(`${text}.amount.label`)}
                disabled={isCustomAmountDisabled}
                textEnd={amountPercentText}
                onFocus={() => {
                  isAmountFieldFocused.current = true
                  amountOnFocus.current = form.getValues('amount')
                }}
                onBlur={() => {
                  isAmountFieldFocused.current = false
                  // Validate and correct on blur
                  const currentAmount = form.getValues('amount')
                  if (currentAmount !== amountOnFocus.current) {
                    isAmountTouched.current = true
                  }
                  const parsed = parse(currentAmount || '')
                  if (isCustomAmount && parsed < minResolved) {
                    form.setValue('amount', `${minResolved}`, {
                      shouldValidate: true,
                    })
                  }
                  if (isCustomAmount && parsed > maxResolved) {
                    form.setValue('amount', `${maxResolved}`, {
                      shouldValidate: true,
                    })
                  }
                }}
              />
              {currentVariantConfig.showSlider && (
                <Slider
                  min={minResolved}
                  max={maxResolved}
                  disabled={isCustomAmountDisabled}
                  minPercentage={NumberUtils.percentage.getSafe(
                    minResolved,
                    maxResolved
                  )}
                  maxPercentage={100}
                  step={amountSlider.step}
                  maxSnapPercent={amountSlider.snapPercent}
                  value={parse(amount || '0')}
                  buttonStart={minPrice}
                  buttonEnd={maxPrice}
                  showPercentageLabel={false}
                  highlightAtMax
                  onChange={(value: number) => {
                    isAmountTouched.current = true
                    form.setValue(
                      'amount',
                      `${value.toFixed(currencyDecimals)}`,
                      {
                        shouldValidate: true,
                      }
                    )
                  }}
                />
              )}
            </InputsSection>
          )}

          {isPayment && (
            <View className="flex flex-col gap-0 overflow-hidden rounded-xl border border-black/10">
              {globemedAmount !== null && (
                <DetailRow
                  size="sm"
                  title={t(`${rows}.globemed.label`)}
                  icon="shield-check-filled"
                  className="border-t-0 border-none"
                >
                  <Price value={globemedAmount} />
                </DetailRow>
              )}
              {showRefundBalance && (
                <DetailRow
                  size="sm"
                  title={t(`${rows}.refundBalance.label`)}
                  icon="cash-banknote-move-back"
                  className={
                    globemedAmount === null
                      ? 'border-t-0 border-none'
                      : undefined
                  }
                >
                  <Price value={refundBalance} />
                </DetailRow>
              )}
              {settings?.rows?.prepayment !== false && (
                <DetailRow
                  size="sm"
                  title={t(`${rows}.prepayment.label`)}
                  icon="receipt-2"
                  iconComponent={
                    <IconPercentage
                      target={prepayment}
                      total={maxResolved}
                      size={24}
                    />
                  }
                  className={
                    !showRefundBalance && globemedAmount === null
                      ? 'border-t-0 border-none'
                      : undefined
                  }
                >
                  <Price value={prepayment} hidden={!isAmountValid} />
                </DetailRow>
              )}
              {settings?.rows?.collect !== false && (
                <DetailRow
                  size="sm"
                  title={t(`${rows}.collect.label`)}
                  icon="clock-dollar"
                  iconComponent={
                    <IconPercentage
                      target={onCollectValue}
                      total={maxResolved}
                      size={24}
                    />
                  }
                >
                  <Price value={onCollectValue} hidden={!isAmountValid} />
                </DetailRow>
              )}
              {!!fee && (
                <DetailRow
                  size="sm"
                  title={t(`${rows}.fee.label`)}
                  iconComponent={
                    <IconPercentage
                      target={fee}
                      total={maxResolved}
                      size={24}
                    />
                  }
                >
                  <Price value={fee} />
                </DetailRow>
              )}
              <DetailRow
                stronger
                size="sm"
                className={
                  !hasPaymentRows ? 'border-t-0 border-none' : undefined
                }
                title={t(`${rows}.total.label`)}
                icon="shopping-bag-filled"
              >
                <Price value={maxResolved} />
              </DetailRow>
            </View>
          )}
          {isInstallment && (
            <InstallmentDetails
              amounts={amounts}
              payment={payment}
              override={
                isUnzer && unzerPlan.data
                  ? {
                      installments: unzerPlan.data.plan.number_of_rates,
                      monthlyAmount: unzerPlan.data.plan.rate_amount,
                      totalAmount: unzerPlan.data.plan.total_amount,
                    }
                  : undefined
              }
            />
          )}

          <BorderSection>
            {isIbanEnabled && (
              <View className="flex flex-col gap-8 py-8">
                <FormInput
                  name="iban"
                  control={form.control}
                  label={t('modals:paymentMethod.iban.label')}
                  mask={INPUT_MASK.IBAN}
                />
                <FormInput
                  name="holder"
                  control={form.control}
                  label={t('modals:paymentMethod.holder.label')}
                />
              </View>
            )}
            {enabledConsents.length > 0 && (
              <View className="flex flex-col gap-8 py-8">
                {enabledConsents.map((consent) => (
                  <FormCheckbox
                    key={consent}
                    control={form.control}
                    name={consent}
                    label={t(`${text}.consent.${consent}.title`)}
                    desc={t(`${text}.consent.${consent}.desc`)}
                  />
                ))}
              </View>
            )}
          </BorderSection>
          {currentProvider === 'cofidisApplication' && (
            <CofidisApplicationFormComponent
              formRef={cofidisApplicationFormRef}
            />
          )}
          <PaymentHelpButton onPress={() => setActionsModalOpen(true)} />
        </View>
        <SpaceButtons />
      </Page>
      <ButtonsFixed>
        {globemed.isFailed ? (
          <Button
            variant="filled"
            label={t('payment:globemed.pending.cta')}
            iconEnd="repeat"
            loading={globemed.metadataQuery.isFetching}
            onPress={globemed.refreshBalance}
          />
        ) : isPayNow ? (
          <PayNowButton
            price={prepayment}
            label={isContinueLabel ? t('ui:continue') : t('ui:payNow')}
            loading={ctaLoading}
            disabled={!payment.method || globemed.isPending}
            onPress={onPress}
          />
        ) : (
          <Button
            variant={prepayment > 0 ? 'outlined' : 'filled'}
            label={
              isContinueLabel
                ? t('ui:continue')
                : t(`payment:settings.method.options.${key}.cta`)
            }
            iconEnd="arrow-narrow-right"
            loading={ctaLoading}
            disabled={!payment.method || globemed.isPending}
            onPress={onPress}
          />
        )}
      </ButtonsFixed>
      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSave={handleSignatureSave}
        isPending={paymentMutation.isPending}
      />
      <PaymentActionsModal
        open={actionsModalOpen}
        orderIds={paymentDisplayIds}
        close={() => setActionsModalOpen(false)}
        onCheckRefundBalance={() => setCheckRefundBalanceOpen(true)}
        onGlobemed={globemed.canAdd ? () => setGlobemedOpen(true) : undefined}
        onRemoveGlobemed={
          globemed.removable ? () => setGlobemedRemoveOpen(true) : undefined
        }
      />
      <CheckRefundBalanceModal
        open={checkRefundBalanceOpen}
        close={() => setCheckRefundBalanceOpen(false)}
        numericOrderIds={orderIds}
        localId={item.localId}
      />
      <GlobeMedAddModal
        open={globemedOpen}
        close={() => setGlobemedOpen(false)}
        numericOrderIds={orderIds}
        localId={item.localId}
        maxAmount={globemedMax}
        isRangeLoading={globemed.metadataQuery.isFetching}
        draftPaymentId={globemed.draftPaymentId}
      />
      {globemed.removable && (
        <GlobeMedRemoveModal
          open={globemedRemoveOpen}
          close={() => setGlobemedRemoveOpen(false)}
          numericOrderIds={orderIds}
          amount={globemed.removable.amount}
          paymentId={globemed.removable.paymentId}
        />
      )}
    </>
  )
}

const Price = ({ value, hidden }: { value: number; hidden?: boolean }) => {
  return (
    <Typography variant="SM" className="pr-6 font-text-semibold text-sm">
      <PriceComponent value={hidden ? 0 : value} showZero={!hidden} />
    </Typography>
  )
}
